import { TKAsyncContent } from '@tk/components/tk.async.content';
import type { PayloadItem, PayloadItemData } from '@tk/utilities/tk.async.content.manager';

export interface StockRequest {
    articles: string;
    quantities: string;
    dimensionVariants: string;
    stockType: number;
    stockLoc?: number;
    withStorages?: boolean;
    disposition?: boolean;
    dispoDate?: string;
}

export interface Storage {
    storageId: string;
    storageName: string;
    stockLocNo: string;
    quantity: string;
    unit: string;
    dimensionVariant: string;
    stockControlTypeCd: string;
    status: string;
    deliveryTime: string;
}

export interface StockResult {
    articleId: string;
    quantity: string;
    unit: string;
    dimensionVariant: string;
    disposition?: boolean;
    dispoDate?: string;
    stockControlTypeCd: string;
    status: string;
    deliveryTime: string;
    storages?: Storage[];
}

export interface StockResponse {
    usecase: 'stock';
    length: number;
    results: StockResult[];
}

/**
 * `<tk-async-stock>` — Web Component for asynchronous stock availability loading.
 *
 * Extends the generic `TKAsyncContent` component with stock-specific defaults
 * and a TSX-based render method.
 *
 * Required attributes:
 *   data-tk-id  — Unique article identifier (Art.InternalNo)
 *   data-tk-url — API endpoint URL (e.g. from gCreateURL(API.lc¦60))
 *
 * Optional attributes (with defaults):
 *   data-tk-usecase        — "stock" (default)
 *   data-tk-cache-strategy — "3600" = 1 h TTL (default)
 *   data-tk-is-lazy        — present = use Intersection Observer (default: absent)
 *   data-tk-root-margin    — "200px" (default)
 *   data-tk-threshold      — 0 (default)
 *   data-tk-show-dispo    — optional boolean flag (also supports data-tk-show-dispo)
 *   data-tk-dispo-date     — optional date (dd.MM.YYYY)
 *   data-tk-show-quantity  — present = render quantity tooltip above label
 *
 * Usage:
 *   <tk-async-stock data-tk-id="123" data-tk-url="/api/stock" data-tk-is-lazy></tk-async-stock>
 */
export default class TKStock extends TKAsyncContent {
    private _escapeTooltipContent(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private _getQuantityTooltipContent(quantity: number, unit?: string, label?: string): string {
        const tooltipLabel = label || this.getTranslation('stock_control', 'Stock');
        const unitLabel = unit ? ` ${unit}` : '';
        return this._escapeTooltipContent(`${tooltipLabel}: ${quantity}${unitLabel}`);
    }

    override get url(): string | null {
        const globalUrl = document.querySelector<HTMLElement>('[data-tk-stock-api]')?.getAttribute('data-tk-url');
        return globalUrl ?? super.url;
    }

    connectedCallback(): void {
        if (!this.hasAttribute('data-tk-usecase')) {
            this.setAttribute('data-tk-usecase', 'stock');
        }
        if (!this.hasAttribute('data-tk-cache-strategy')) {
            this.setAttribute('data-tk-cache-strategy', '3600');
        }
        super.connectedCallback();
    }

    protected override getBatchKey(): string {
        return JSON.stringify({
            stockType: parseInt(this.getAttribute('data-tk-stock-type') ?? '1', 10),
            stockLoc: this.getAttribute('data-tk-stock-loc') ?? null,
            withStorages: this.hasAttribute('data-tk-with-storages'),
            disposition: this.hasAttribute('data-tk-show-dispo'),
            dispoDate: this.getAttribute('data-tk-dispo-date') ?? null,
        });
    }

    // ─── Stock-specific request params ───────────────────────────────────────

    /**
     * Returns the per-item data that varies between product instances:
     * dimensionVariant and quantity.
     * Stock-level parameters that apply to all items in the batch
     * (stockType, stockLoc, withStorages) are read in `buildPayload`.
     */
    protected override getItemData(): PayloadItemData {
        return {
            dimensionVariant: this.getAttribute('data-tk-dimension-variant') ?? '0',
            quantity: this.getAttribute('data-tk-quantity') ?? '1',
            disposition: this.hasAttribute('data-tk-show-dispo'),
            dispoDate: this.getAttribute('data-tk-dispo-date') ?? undefined,
        };
    }

    /**
     * Assembles the Stock API POST body from all batched items.
     * Batch-level options (stockType, stockLoc, withStorages) are read from the
     * first element in the batch — callers must ensure these are consistent
     * across elements sharing the same queue (same url + usecase).
     */
    protected override buildPayload(items: PayloadItem[]): Record<string, unknown> {
        return {
            usecase: 'stock',
            articles: items.map((i) => i.id).join(','),
            dimensionVariants: items.map((i) => String(i.itemData?.['dimensionVariant'] ?? '0')).join(','),
            quantities: items.map((i) => String(i.itemData?.['quantity'] ?? '1')).join(','),
            stockType: parseInt(this.getAttribute('data-tk-stock-type') ?? '1', 10),
            stockLoc: this.getAttribute('data-tk-stock-loc') ?? undefined,
            withStorages: this.hasAttribute('data-tk-with-storages'),
            disposition: this.hasAttribute('data-tk-show-dispo') ?? undefined,
            dispoDate: this.getAttribute('data-tk-dispo-date') ?? undefined,
        };
    }

    private _getStatusLabel(status: string): string {
        return this.getTranslation(status, status);
    }

    public getStockIcon(status: string): string {
        const iconMap: Record<string, string> = {
            available: 'check',
            'less-stock': 'remove',
            'out-of-stock': 'cross',
            'na-stock': 'not-disturb',
            'call-stock': 'call',
        };

        return (
            <svg>
                <use
                    href={`/webportal/config/layout/themes/project/assets/fonts/generated/tk-icons.symbol.svg#tk-icon-${iconMap[status] ?? 'cross'}`}
                ></use>
            </svg>
        );
    }

    public getStockComponent({
        status,
        label,
        quantity,
        unit,
        showQuantityTooltip,
        tooltipStatus,
    }: {
        status: string;
        label?: string;
        quantity?: number;
        unit?: string;
        showQuantityTooltip?: boolean;
        tooltipStatus?: string;
    }): string {
        label = label ?? this._getStatusLabel(status || '');
        const rootClass = {
            'tk-stock': true,
            'tk-stock--on-stock': status === 'available',
            'tk-stock--out-stock': status === 'out-of-stock',
            'tk-stock--less-stock': status === 'less-stock',
            'tk-stock--na-stock': status === 'na-stock',
            'tk-stock--call-stock': status === 'call-stock',
            'tk-stock--with-quantity': quantity !== undefined && unit,
        };

        const tooltipClass = {
            'tk-stock': true,
            'tk-stock--on-stock': (tooltipStatus ?? status) === 'available',
            'tk-stock--out-stock': (tooltipStatus ?? status) === 'out-of-stock',
            'tk-stock--less-stock': (tooltipStatus ?? status) === 'less-stock',
            'tk-stock--na-stock': (tooltipStatus ?? status) === 'na-stock',
            'tk-stock--call-stock': (tooltipStatus ?? status) === 'call-stock',
            'tk-stock__quantity-tooltip': true,
        };

        if (showQuantityTooltip && quantity !== undefined && unit) {
            return (
                <tk-tooltip
                    data-tk-tooltip-options="{'placement':'auto-start'}"
                    data-tk-tooltip-content={this._getQuantityTooltipContent(quantity, unit, label)}
                    class={tooltipClass}
                >
                    <div class={rootClass}>
                        <span class="tk-stock__icon">{this.getStockIcon(status || '')}</span>
                        <span class="tk-stock__label">{label}</span>
                    </div>
                </tk-tooltip>
            );
        } else {
            return (
                <div class={rootClass}>
                    <span class="tk-stock__icon">{this.getStockIcon(status || '')}</span>
                    <span class="tk-stock__label">{label}</span>
                    {quantity !== undefined && unit && (
                        <span class="tk-stock__quantity">
                            {quantity} {unit}
                        </span>
                    )}
                </div>
            );
        }
    }

    public render(data: unknown): string {
        const stockData = data as StockResult;

        if (!stockData) {
            return <div class="tk-stock tk-stock--error" />;
        }

        const storages = this.hasAttribute('data-tk-with-storages') ? stockData.storages : undefined;
        const checkStockLabel = this.getTranslation('check_stock');
        const stockControlLabel = this.getTranslation('stock_control');
        const stockQuantity = parseFloat(stockData.quantity);
        const showQuantity = this.hasAttribute('data-tk-show-quantity');

        return (
            <>
                {this.getStockComponent({
                    status: stockData.status,
                    quantity: showQuantity ? stockQuantity : undefined,
                    unit: stockData.unit,
                    showQuantityTooltip: showQuantity,
                    tooltipStatus: 'available',
                })}

                {storages && storages.length > 0 && (
                    <tk-popup>
                        <button class="tk-button tk-button--link">
                            <span class="tk-button__label">{checkStockLabel}</span>
                            <span class="tk-button__icon">
                                <i class="tk-icon-chevron-right"></i>
                            </span>
                        </button>
                        <template>
                            <div class="tk-stock-control flex flex--col flex--gap-4">
                                <span class="typ__body typ__body--bold">{stockControlLabel}</span>
                                <div class="flex flex--col">
                                    {storages.map((storage) => (
                                        <div class="flex">
                                            {this.getStockComponent({
                                                status: storage.status,
                                                label: storage.storageName,
                                                quantity: parseFloat(storage.quantity),
                                                unit: storage.unit,
                                            })}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </template>
                    </tk-popup>
                )}
            </>
        );
    }
}
